import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@sharedComponents/DesignSystem/Inputs';
import { OriginEditorContainer } from '@components/Pages/LettersPage/OriginEditorContainer';
import {
  canUpdateTemplateEditorSelector,
  currentSubmissionLoadingSelector,
  currentSubmissionOriginSelector,
  currentSubmissionTemplateEditorFormChanged,
} from '@selectors/submission';
import {
  createNewSectionsAndBlocksByCurrentSubmissionOriginId,
  createSubmissionOriginSection,
  resetUnsavedChangesTemplateEditorForm,
  setCurrentSubmissionTemplateEditorMode,
} from '@actions/submissionActions';
import { UpdateSampleModal } from '@components/Modals';
import { TextField, Button as MuiButton } from '@material-ui/core';
import { AddRoundIcon, DeleteRoundIcon, Save, Reset, WellDone } from '@images/icons';
import { translate } from '@utils';

import './index.scss';

export const TemplateEditor = () => {
  const [isCreatedSection, setIsCreatedSection] = useState(false);
  const [createdSectionName, setCreatedSectionName] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const dispatch = useDispatch();

  const isCurrentSubmissionTemplateEditorFormChanged = useSelector(
    currentSubmissionTemplateEditorFormChanged
  );
  const isCurrentSubmissionLoading = useSelector(currentSubmissionLoadingSelector);
  const currentSubmissionOrigin = useSelector(currentSubmissionOriginSelector);
  const canUpdateTemplateEditor = useSelector(canUpdateTemplateEditorSelector);

  useEffect(() => {
    dispatch(setCurrentSubmissionTemplateEditorMode(true));
    const handleKeyPress = (event) => {
      if (event.keyCode === 116) {
        event.preventDefault();
        handleResetTemplateEditorChanges();
      }
    };
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      dispatch(setCurrentSubmissionTemplateEditorMode(false));
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const handleSaveTemplateEditorChanges = useCallback(() => {
    dispatch(createNewSectionsAndBlocksByCurrentSubmissionOriginId());
  }, [dispatch]);

  const handleResetTemplateEditorChanges = () => {
    dispatch(resetUnsavedChangesTemplateEditorForm());
  };

  const handleCreateSection = () => {
    setIsCreatedSection(false);
    dispatch(createSubmissionOriginSection(createdSectionName));
    setCreatedSectionName('');
  };

  const handleInputChange = ({ target }) => {
    setCreatedSectionName(target.value);
  };

  const handleOpenModal = () => {
    setIsOpenModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <>
      <div className="template-editor-wrapper">
        <div className="create-section-input-container" data-testid="create-section-container">
          {isCreatedSection ? (
            <>
              <MuiButton size="large" onClick={() => setIsCreatedSection(false)} disableRipple>
                <DeleteRoundIcon />
              </MuiButton>
              <TextField
                data-testid="section-name-input"
                name={'section-name'}
                onChange={handleInputChange}
                value={createdSectionName}
              />
              <MuiButton
                data-testid="create-section-button"
                color="primary"
                onClick={handleCreateSection}
                disabled={!createdSectionName}
                disableRipple
              >
                <WellDone />
              </MuiButton>
            </>
          ) : (
            <>
              <MuiButton
                data-testid="add-section-button"
                size="large"
                onClick={() => setIsCreatedSection(true)}
                disableRipple
              >
                <AddRoundIcon />
              </MuiButton>
              <span className="add-section-field">{translate('add_new_section')}</span>
            </>
          )}
        </div>
        <div className="template-editor-content" data-testid="template-editor">
          <OriginEditorContainer />
        </div>

        <div className="template-buttons" data-testid="template-buttons">
          <Button
            testId="save-template-editor-changes"
            startIcon={<Save />}
            type="basic"
            disabled={!isCurrentSubmissionTemplateEditorFormChanged || !canUpdateTemplateEditor}
            onClick={handleOpenModal}
          >
            {translate('save')}
          </Button>
          <Button
            testId="reset-template-editor-changes"
            startIcon={<Reset />}
            type="stroke"
            disabled={!isCurrentSubmissionTemplateEditorFormChanged}
            onClick={handleResetTemplateEditorChanges}
          >
            {translate('reset')}
          </Button>
        </div>
      </div>
      <UpdateSampleModal
        isOpenSaveTemplateModal={isOpenModal}
        handleCancel={handleCloseModal}
        handleConfirmation={handleSaveTemplateEditorChanges}
        isLoading={isCurrentSubmissionLoading}
        origin={currentSubmissionOrigin}
      />
    </>
  );
};
